import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Skeleton,
  Alert,
  IconButton,
  Pagination,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Store as StoreIcon,
  ShoppingCart as ShoppingCartIcon,
  Apps as AppsIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  GridView as GridViewIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ownerProfileService from '../services/ownerProfileService';
import SearchResultCard from '../components/search/SearchResultCard';

const ITEMS_PER_PAGE = 12;

const OwnerProfilePage = () => {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  // ─── State ───
  const [owner, setOwner] = useState(null);
  const [listings, setListings] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalListings, setTotalListings] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  // Compteurs globaux pour le header (mis à jour uniquement depuis type='all')
  const [headerListingsCount, setHeaderListingsCount] = useState(0);
  const [headerProductsCount, setHeaderProductsCount] = useState(0);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingItems, setLoadingItems] = useState(true);
  const [error, setError] = useState(null);

  // Tab: 0 = all, 1 = listings, 2 = products
  // Si ?tab=products dans l'URL (ex: lien pub Facebook), ouvrir directement sur Produits
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') === 'products' ? 2 : 0;
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Filtres (input en cours d'édition)
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [city, setCity] = useState('');
  const [condition, setCondition] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filtres appliqués (déclenchent l'appel API)
  const [appliedFilters, setAppliedFilters] = useState({
    searchQuery: '', minPrice: '', maxPrice: '', city: '', condition: '', sortBy: 'date', sortOrder: 'desc'
  });

  const typeMap = { 0: 'all', 1: 'listings', 2: 'products' };

  // ─── Fetch profile ───
  const fetchOwnerProfile = useCallback(async () => {
    try {
      setLoadingProfile(true);
      const data = await ownerProfileService.getOwnerProfile(ownerId);
      setOwner(data.owner);
    } catch (err) {
      setError(err?.response?.data?.message || t('owner.error.profile', 'Impossible de charger ce profil'));
    } finally {
      setLoadingProfile(false);
    }
  }, [ownerId, t]);

  // ─── Fetch items ───
  const fetchOwnerItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      const backendSortBy = appliedFilters.sortBy === 'date' ? 'createdAt' : appliedFilters.sortBy;
      const params = {
        type: typeMap[activeTab],
        sortBy: backendSortBy,
        sortOrder: appliedFilters.sortOrder,
        limit: ITEMS_PER_PAGE,
        page: currentPage
      };
      if (appliedFilters.searchQuery.trim()) params.search = appliedFilters.searchQuery.trim();
      if (appliedFilters.minPrice) params.minPrice = appliedFilters.minPrice;
      if (appliedFilters.maxPrice) params.maxPrice = appliedFilters.maxPrice;
      if (appliedFilters.city.trim()) params.city = appliedFilters.city.trim();
      // condition seulement si onglet produits
      if (appliedFilters.condition && activeTab === 2) params.condition = appliedFilters.condition;

      const data = await ownerProfileService.getOwnerItems(ownerId, params);
      setListings(data.listings || []);
      setProducts(data.products || []);
      setTotalListings(data.totalListings || 0);
      setTotalProducts(data.totalProducts || 0);
      // Mettre à jour les compteurs header uniquement depuis type='all'
      if (typeMap[activeTab] === 'all') {
        setHeaderListingsCount(data.totalListings || 0);
        setHeaderProductsCount(data.totalProducts || 0);
      }
    } catch (err) {
      console.error('[OwnerProfilePage] Error fetching items:', err);
    } finally {
      setLoadingItems(false);
    }
  }, [ownerId, activeTab, currentPage, appliedFilters]);

  useEffect(() => { fetchOwnerProfile(); }, [fetchOwnerProfile]);
  useEffect(() => { fetchOwnerItems(); }, [fetchOwnerItems]);

  // Nettoyer ?tab=products de l'URL après le premier render
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'products') {
      params.delete('tab');
      const newSearch = params.toString();
      navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Computed ───
  const totalItems = useMemo(() => {
    if (activeTab === 1) return totalListings;
    if (activeTab === 2) return totalProducts;
    return totalListings + totalProducts;
  }, [activeTab, totalListings, totalProducts]);

  const totalPages = useMemo(() => {
    if (activeTab === 1) return Math.max(1, Math.ceil(totalListings / ITEMS_PER_PAGE));
    if (activeTab === 2) return Math.max(1, Math.ceil(totalProducts / ITEMS_PER_PAGE));
    // En mode Tout : utiliser le max des deux pour éviter les pages vides
    return Math.max(1, Math.ceil(Math.max(totalListings, totalProducts) / ITEMS_PER_PAGE));
  }, [activeTab, totalListings, totalProducts]);

  // ─── Handlers ───
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setCurrentPage(1);
    // Reset condition quand on quitte l'onglet produits
    if (newValue !== 2) {
      setCondition('');
    }
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({ searchQuery, minPrice, maxPrice, city, condition, sortBy, sortOrder });
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setMinPrice('');
    setMaxPrice('');
    setCity('');
    setCondition('');
    setSortBy('date');
    setSortOrder('desc');
    setCurrentPage(1);
    setAppliedFilters({ searchQuery: '', minPrice: '', maxPrice: '', city: '', condition: '', sortBy: 'date', sortOrder: 'desc' });
  };

  const handleSortOrderToggle = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    setSortOrder(newOrder);
    setCurrentPage(1);
    setAppliedFilters((prev) => ({ ...prev, sortOrder: newOrder, sortBy }));
  };

  const handleSortByChange = (e) => {
    const newSort = e.target.value;
    setSortBy(newSort);
    setCurrentPage(1);
    setAppliedFilters((prev) => ({ ...prev, sortBy: newSort }));
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = useMemo(() => {
    return !!(appliedFilters.searchQuery || appliedFilters.minPrice || appliedFilters.maxPrice || appliedFilters.city || appliedFilters.condition);
  }, [appliedFilters]);

  // ─── Render ───
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="outlined" onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
            {t('common.back', 'Retour')}
          </Button>
        </Box>
      </Container>
    );
  }

  const mapItem = (item, forceProduct) => {
    const isProductItem = forceProduct || !!item.seller_id || (item.price_cents !== undefined && item.condition !== undefined);
    return {
      ...item,
      _isProduct: isProductItem,
      owner: owner
        ? { id: owner.id, name: owner.name, profilePicture: owner.profilePicture }
        : item.owner || item.seller
    };
  };

  const displayListings = listings.map((item) => mapItem(item, false));
  const displayProducts = products.map((item) => mapItem(item, true));
  const displayItems = activeTab === 1 ? displayListings
    : activeTab === 2 ? displayProducts
    : [...displayListings, ...displayProducts];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7f7f7', pb: 8 }}>
      {/* ─── Header ─── */}
      <Paper
        elevation={0}
        sx={{ borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper', pt: { xs: 3, md: 5 }, pb: { xs: 2, md: 3 } }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: { xs: 0, md: 1 } }}>
              <ArrowBackIcon />
            </IconButton>

            {loadingProfile ? (
              <Skeleton variant="circular" width={80} height={80} />
            ) : (
              <Avatar
                src={owner?.profilePicture}
                sx={{ width: { xs: 64, md: 80 }, height: { xs: 64, md: 80 }, fontSize: { xs: 24, md: 32 } }}
              >
                {(owner?.name || 'U')[0]}
              </Avatar>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {loadingProfile ? (
                <>
                  <Skeleton variant="text" width={200} height={32} />
                  <Skeleton variant="text" width={120} height={20} />
                </>
              ) : (
                <>
                  <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                    {owner?.name || t('owner.anonymous', 'Propriétaire')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('owner.memberSince', 'Membre depuis {{date}}', {
                      date: owner?.createdAt ? new Date(owner.createdAt).toLocaleDateString() : '—'
                    })}
                  </Typography>
                </>
              )}
            </Box>

            {!loadingProfile && (
              <Box sx={{ display: 'flex', gap: 2, ml: 'auto', flexWrap: 'wrap' }}>
                <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: 3, border: '1px solid #ececec', textAlign: 'center', minWidth: 80 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25 }}>
                    <StoreIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight={800} color="primary.main">{headerListingsCount || owner?.stats?.listingsCount || 0}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{t('owner.listings', 'Annonces')}</Typography>
                </Paper>
                <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: 3, border: '1px solid #ececec', textAlign: 'center', minWidth: 80 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25 }}>
                    <ShoppingCartIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="h6" fontWeight={800} color="success.main">{headerProductsCount || owner?.stats?.productsCount || 0}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{t('owner.products', 'Produits')}</Typography>
                </Paper>
              </Box>
            )}
          </Box>
        </Container>
      </Paper>

      {/* ─── Tabs + Filters ─── */}
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #ececec', overflow: 'hidden', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1, flexWrap: 'wrap', gap: 1 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant={isMobile ? 'fullWidth' : 'standard'}
              sx={{ minWidth: 0 }}
            >
              <Tab icon={<AppsIcon fontSize="small" />} iconPosition="start" label={t('owner.all', 'Tout')} />
              <Tab icon={<StoreIcon fontSize="small" />} iconPosition="start" label={t('owner.listingsTab', 'Annonces')} />
              <Tab icon={<ShoppingCartIcon fontSize="small" />} iconPosition="start" label={t('owner.productsTab', 'Produits')} />
            </Tabs>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {/* Tri rapide */}
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={sortBy}
                  onChange={handleSortByChange}
                  displayEmpty
                  sx={{ borderRadius: 2, fontSize: '0.85rem' }}
                >
                  <MenuItem value="date">{t('owner.sort.dateRecent', 'Plus récent')}</MenuItem>
                  <MenuItem value="price">{t('owner.sort.price', 'Prix')}</MenuItem>
                  <MenuItem value="rating">{t('owner.sort.rating', 'Note')}</MenuItem>
                  <MenuItem value="title">{t('owner.sort.title', 'Titre A-Z')}</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={handleSortOrderToggle}
                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
                title={sortOrder === 'desc' ? t('owner.sort.descending', 'Décroissant') : t('owner.sort.ascending', 'Croissant')}
              >
                {sortOrder === 'desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
              </IconButton>

              {hasActiveFilters && (
                <Chip
                  label={t('owner.activeFilters', 'Filtres actifs')}
                  color="primary"
                  size="small"
                  onDelete={handleClearFilters}
                  deleteIcon={<ClearIcon />}
                />
              )}
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters((v) => !v)}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {t('owner.filters', 'Filtres')}
              </Button>
            </Box>
          </Box>

          {/* Filters panel */}
          {showFilters && (
            <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'rgba(0,0,0,0.02)', borderTop: `1px solid ${theme.palette.divider}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('owner.search', 'Rechercher')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                    InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField fullWidth size="small" type="number" label={t('owner.minPrice', 'Prix min')} value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField fullWidth size="small" type="number" label={t('owner.maxPrice', 'Prix max')} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField fullWidth size="small" label={t('owner.city', 'Ville')} value={city} onChange={(e) => setCity(e.target.value)} />
                </Grid>
                {activeTab === 2 && (
                  <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('owner.condition', 'État')}</InputLabel>
                      <Select value={condition} label={t('owner.condition', 'État')} onChange={(e) => setCondition(e.target.value)}>
                        <MenuItem value="">{t('owner.conditionAll', 'Tous')}</MenuItem>
                        <MenuItem value="new">{t('owner.conditionNew', 'Neuf')}</MenuItem>
                        <MenuItem value="used">{t('owner.conditionUsed', 'Occasion')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={1}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" size="small" fullWidth onClick={handleApplyFilters} sx={{ borderRadius: 2, textTransform: 'none' }}>
                      {t('owner.apply', 'OK')}
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ borderRadius: 2, textTransform: 'none', minWidth: 40 }}>
                      <ClearIcon fontSize="small" />
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        {/* ─── Results info ─── */}
        {!loadingItems && displayItems.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                bgcolor: 'primary.main', color: 'white',
                px: 1.5, py: 0.5, borderRadius: 2,
                fontSize: '0.8rem', fontWeight: 700
              }}>
                <GridViewIcon sx={{ fontSize: 15 }} />
                {totalItems}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {activeTab === 1 ? t('owner.listings', 'Annonces') : activeTab === 2 ? t('owner.products', 'Produits') : t('owner.all', 'éléments')}
              </Typography>
            </Box>
            {totalPages > 1 && (
              <Chip
                label={t('owner.pageInfo', 'Page {{current}} / {{total}}', { current: currentPage, total: totalPages })}
                size="small"
                variant="outlined"
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: '0.75rem' }}
              />
            )}
          </Box>
        )}

        {/* ─── Results Grid ─── */}
        {loadingItems ? (
          <Grid container spacing={2}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : displayItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('owner.noResults', 'Aucun résultat')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('owner.noResultsHint', 'Aucun élément à afficher pour ce propriétaire.')}
            </Typography>
            {hasActiveFilters && (
              <Button variant="outlined" sx={{ mt: 2 }} onClick={handleClearFilters}>
                {t('owner.clearFilters', 'Effacer les filtres')}
              </Button>
            )}
          </Box>
        ) : (
          <>
            {/* En mode Tout : sections séparées listings / produits */}
            {activeTab === 0 ? (
              <>
                {displayListings.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <StoreIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={700}>
                        {t('owner.listings', 'Annonces')}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({totalListings})
                        </Typography>
                      </Typography>
                    </Box>
                    <Grid container spacing={2} sx={{ mb: displayProducts.length > 0 ? 4 : 0 }}>
                      {displayListings.map((item) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`l-${item.id}`}>
                          <SearchResultCard listing={item} layout="airbnb" isProduct={false} cardSize="tall" onSelect={() => {}} />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                {displayProducts.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <ShoppingCartIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={700}>
                        {t('owner.products', 'Produits')}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({totalProducts})
                        </Typography>
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {displayProducts.map((item) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`p-${item.id}`}>
                          <SearchResultCard listing={item} layout="airbnb" isProduct={true} cardSize="tall" onSelect={() => {}} />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </>
            ) : (
              <Grid container spacing={2}>
                {displayItems.map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={`${item._isProduct ? 'p' : 'l'}-${item.id}`}>
                    <SearchResultCard
                      listing={item}
                      layout="airbnb"
                      isProduct={item._isProduct}
                      cardSize="tall"
                      onSelect={() => {}}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* ─── Pagination ─── */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default OwnerProfilePage;
